<template>
  <div
    class="nearest-premium-wrap w-full md:max-w-screen-xl grid mx-auto pt-3 px-2 sm:px-6 lg:px-0 overflow-hidden pb-6 mb-4 md:mb-0 md:mt-6 lg:mt-0 overflow-hidden space-y-3 md:space-y-4 px-3 lg:px-6"
    :style="{ minHeight: '210px' }"
  >
    <div class="nearest-program-hero">
      <span class="nearest-program-copy">
        <p class="nearest-program-kicker">
          <font-awesome-icon icon="fa-solid fa-calendar-check" />
          {{ $t('Güncel kalkışlar ve açık kontenjanlar') }}
        </p>
        <h2 class="nearest-program-title">
          {{ $t('Yaklaşan Umre Programları') }}
        </h2>
        <p class="nearest-program-description">{{ $t('Kalkış tarihini, otel seçeneğini ve kişi başı fiyatı aynı ekranda karşılaştırın.') }}</p>
      </span>
      <button
        @click.prevent.stop="goPath('/tour/umre-turlari')"
        class="nearest-allprices-btn"
      >
        <span>{{ $t('Tüm Programlar') }}</span>
        <ChevronRightIcon class="w-4 h-4" />
      </button>
    </div>

    <div v-if="loading" class="nearest-loading-state">
      <div class="animate-spin inline-flex items-center justify-center h-12 w-12 rounded-full border-2 border-neutral-300 border-t-accent-500"></div>
      <span class="mt-3 text-sm text-neutral-700">{{ $t('Loading') }}</span>
    </div>

    <div v-if="nextTours.length > 0" class="nearest-program-content">
      <Carousel
        v-if="isMobile"
        class="nearest-mobile-carousel carousel carousel-pagination-bottom max-w-full"
        :itemsToShow="1"
        :wrapAround="visibleTours.length > 1"
        :transition="450"
        :mouseDrag="true"
        :touchDrag="true"
        snapAlign="start"
      >
        <Slide
          v-for="(dateGroup, dateIndex) in visibleTours"
          :key="`date-group-mobile-${dateIndex}`"
          class="px-0.5"
        >
          <div class="nearest-date-card w-full rounded-2xl border border-neutral-300 bg-white p-3 shadow-[0_14px_28px_-20px_rgba(22,32,51,0.45)]">
            <div class="nearest-date-head mb-2 rounded-xl border border-brand-200 bg-gradient-to-r from-brand-50 via-white to-black-50 px-3 py-2 flex items-center justify-between gap-2">
              <div class="flex items-center gap-3 min-w-0">
                <div class="rounded-lg border border-brand-200 bg-white px-2 py-1 text-center min-w-[56px] shadow-sm">
                  <div class="text-lg leading-4 font-bold text-brand-800">{{ getDateDay(dateGroup.turs_StartDate) }}</div>
                  <div class="text-[10px] font-semibold text-brand-600">{{ getDateMonth(dateGroup.turs_StartDate) }}</div>
                </div>
                <div class="min-w-0">
                  <div class="text-xs font-semibold text-brand-800 text-left truncate">{{ getDateWeekday(dateGroup.turs_StartDate) }}</div>
                  <div class="text-[12px] text-neutral-600 text-left truncate">{{ dateGroup.start_mini }}</div>
                </div>
              </div>
              <div class="text-[11px] font-semibold text-black-700 bg-black-50 border border-black-200 rounded-full px-2 py-0.5 shrink-0">
                {{ dateGroup.tour?.length || 0 }} {{ $t('tur') }}
              </div>
            </div>

            <div class="space-y-2 max-h-[130px] overflow-y-auto nearest-option-list">
              <button
                v-for="(nt, ntIndex) in dateGroup.tour"
                :key="`opt-mobile-${dateIndex}-${ntIndex}`"
                type="button"
                @click="getBestSure(nt?.sure) && goPath(`/tourDetail/${getBestSure(nt?.sure).id}`)"
                class="nearest-option-card w-full text-left rounded-xl border border-neutral-300 bg-neutral-50/60 p-2.5 hover:border-accent-300 hover:bg-accent-50/30"
                :disabled="!getBestSure(nt?.sure)"
              >
                <div class="flex items-center justify-between gap-2 mb-1.5">
                  <span v-if="dateGroup.tour.length > 1" class="inline-flex items-center -mt-4 text-[11px] font-semibold text-accent-700 bg-accent-50 border border-accent-200 rounded-full px-2 py-0.5">
                    {{ ntIndex + 1 }}. {{ $t('Program') }}
                  </span> 
                  <span class="flex items-center gap-0.5">
                    <font-awesome-icon icon="fa-solid fa-star" class="h-3 w-3 text-yellow-500" />
                    <font-awesome-icon icon="fa-solid fa-star" class="h-3 w-3 text-yellow-500" />
                    <font-awesome-icon icon="fa-solid fa-star" class="h-3 w-3 text-yellow-500" />
                    <font-awesome-icon icon="fa-solid fa-star" class="h-3 w-3 text-yellow-500" />
                    <font-awesome-icon icon="fa-solid fa-star" class="h-3 w-3 text-yellow-500" />
                  </span>
                  <img
                    v-if="nt?.airwayLogo"
                    :src="showImage(nt?.airwayLogo, 100)"
                    :title="nt?.airwayName"
                    :alt="nt?.airwayName"
                    width="100"
                    height="16"
                    class="h-4 w-auto"
                  />
                </div>

                <div class="flex items-start gap-2 mb-1.5">
                  <div class="flex -space-x-2 shrink-0">
                    <img
                      :src="showImage(nt?.otel1_photo, 120, 120)"
                      :title="nt.otel1"
                      :alt="nt.otel1"
                      class="h-10 w-10 rounded-full ring-2 ring-white shadow"
                    />
                    <img
                      v-if="nt?.otel2_photo"
                      :src="showImage(nt?.otel2_photo, 120, 120)"
                      :title="nt.otel2"
                      :alt="nt.otel2"
                      class="h-10 w-10 rounded-full ring-2 ring-white shadow"
                    />
                  </div>

                  <div class="min-w-0 grow text-left">
                    <div class="text-xs font-semibold text-neutral-800 truncate text-left">
                      <span class="text-neutral-500 font-normal">{{ nt?.otel1_city }}:</span>
                      <span v-html="nt.otel1"></span>
                    </div>
                    <div v-if="nt?.otel2" class="text-xs font-semibold text-neutral-700 truncate text-left">
                      <span class="text-neutral-500 font-normal">{{ nt?.otel2_city }}:</span>
                      <span v-html="nt?.otel2"></span>
                    </div>
                  </div>
                </div>

                <div v-if="getBestSure(nt?.sure)" class="rounded-lg border border-neutral-200 bg-white px-2 py-1.5">
                  <div class="flex items-center justify-between">
                    <div class="text-xs text-neutral-600">
                      <span class="font-semibold text-neutral-800">{{ getBestSure(nt?.sure).sure }} {{ $t('gün') }}</span>
                      <span class="mx-1 text-neutral-400">•</span>
                      <span class="text-neutral-500">{{ $t('Dönüş') }}: {{ getBestSure(nt?.sure).end_mini }}</span>
                    </div>
                    <div class="text-right flex items-center gap-1 justify-end min-w-0">
                      <div class="text-[11px] text-neutral-400 line-through mr-2"
                        v-if="getBestSure(nt?.sure).price_high && getBestSure(nt?.sure).price < getBestSure(nt?.sure).price_high">
                        {{ formatMoney(getBestSure(nt?.sure).price_high) }}
                        {{ getCurrencySymbol(getBestSure(nt?.sure).currency) }}
                      </div>
                      <div class="text-sm font-bold text-black-700">
                        {{ formatMoney(getBestSure(nt?.sure).price) }}
                        {{ getCurrencySymbol(getBestSure(nt?.sure).currency) }}
                      </div>
                    </div>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </Slide>
        <template #addons>
          <Pagination></Pagination>
        </template>
      </Carousel>

      <div v-else class="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 gap-3">
        <article
          v-for="(dateGroup, dateIndex) in visibleTours"
          :key="`date-group-${dateIndex}`"
          class="nearest-date-card rounded-2xl border border-neutral-300 bg-white p-3 shadow-[0_14px_28px_-20px_rgba(22,32,51,0.45)]"
        >
          <div class="nearest-date-head mb-2 rounded-xl border border-accent-200 bg-gradient-to-r from-accent-200 to-black-50 px-3 py-2 flex items-center justify-between gap-2">
            <div class="flex items-center gap-3 min-w-0">
              <div class="rounded-lg border border-accent-200 bg-white px-2 py-1 text-center min-w-[56px] shadow-sm">
                <div class="text-lg leading-4 font-bold text-yellow-800">{{ getDateDay(dateGroup.turs_StartDate) }}</div>
                <div class="text-[10px] font-semibold text-yellow-600">{{ getDateMonth(dateGroup.turs_StartDate) }}</div>
              </div>
              <div class="min-w-0">
                <div class="text-xs font-semibold text-yellow-800 text-left truncate">{{ getDateWeekday(dateGroup.turs_StartDate) }}</div>
                <div class="text-[12px] text-neutral-600 text-left truncate">{{ dateGroup.start_mini }}</div>
              </div>
            </div>
            <div class="text-[11px] font-semibold text-black-700 bg-black-50 border border-black-200 rounded-full px-2 py-0.5 shrink-0">
              {{ dateGroup.tour?.length || 0 }} {{ $t('tur') }}
            </div>
          </div>

          <div class="space-y-2 max-h-[150px] overflow-y-auto nearest-option-list">
            <button
              v-for="(nt, ntIndex) in dateGroup.tour"
              :key="`opt-${dateIndex}-${ntIndex}`"
              type="button"
              @click="getBestSure(nt?.sure) && goPath(`/tourDetail/${getBestSure(nt?.sure).id}`)"
              class="nearest-option-card w-full text-left rounded-xl border border-neutral-300 bg-neutral-50/60 p-2.5 hover:border-accent-300 hover:bg-accent-50/30"
              :disabled="!getBestSure(nt?.sure)"
            >
              <div class="flex items-center justify-between gap-2 pb-1 mb-1.5">
               
                 <span v-if="dateGroup.tour.length > 1" class="inline-flex items-center text-[11px] font-semibold text-accent-800">
                    {{ ntIndex + 1 }}. {{ $t('Program') }}
                  </span> 
                 <span class="flex">
                  <font-awesome-icon  icon="fa-solid fa-star" class="h-3 w-3 text-yellow-500" />
                  <font-awesome-icon  icon="fa-solid fa-star" class="h-3 w-3 text-yellow-500" />
                  <font-awesome-icon  icon="fa-solid fa-star" class="h-3 w-3 text-yellow-500" />
                  <font-awesome-icon  icon="fa-solid fa-star" class="h-3 w-3 text-yellow-500" />
                  <font-awesome-icon  icon="fa-solid fa-star" class="h-3 w-3 text-yellow-500" />
                </span>
                <img
                  v-if="nt?.airwayLogo"
                  :src="showImage(nt?.airwayLogo, 100)"
                  :title="nt?.airwayName"
                  :alt="nt?.airwayName"
                  width="100"
                  height="16"
                  class="h-4 w-auto"
                />
               
              </div>

              <div class="flex items-start gap-2 mb-1.5">
                <div class="flex -space-x-2 shrink-0">
                  <img
                    :src="showImage(nt?.otel1_photo, 120, 120)"
                    :title="nt.otel1"
                    :alt="nt.otel1"
                    class="h-10 w-10 rounded-full ring-2 ring-white shadow"
                  />
                  <img
                    v-if="nt?.otel2_photo"
                    :src="showImage(nt?.otel2_photo, 120, 120)"
                    :title="nt.otel2"
                    :alt="nt.otel2"
                    class="h-10 w-10 rounded-full ring-2 ring-white shadow"
                  />
                </div>

                <div class="min-w-0 grow text-left">
                  <div class="text-xs font-semibold text-neutral-800 truncate text-left">
                    <span class="text-neutral-500 font-normal">{{ nt?.otel1_city }}:</span>
                    <span v-html="nt.otel1"></span>
                  </div>
                  <div v-if="nt?.otel2" class="text-xs font-semibold text-neutral-700 truncate text-left">
                    <span class="text-neutral-500 font-normal">{{ nt?.otel2_city }}:</span>
                    <span v-html="nt?.otel2"></span>
                  </div>
                </div>
              </div>

              <div v-if="getBestSure(nt?.sure)" class="rounded-lg border border-neutral-200 bg-white px-2 py-1.5">
                <div class="flex items-center justify-between">
                  <div class="text-xs text-neutral-600">
                    <span class="font-semibold text-neutral-800">{{ getBestSure(nt?.sure).sure }} {{ $t('gün') }}</span>
                    <span class="mx-1 text-neutral-400">•</span>
                    <span class="text-neutral-500">{{ $t('Dönüş') }}: {{ getBestSure(nt?.sure).end_mini }}</span>
                  </div>
                  <div class="text-right flex items-center gap-1 justify-end min-w-0">
                    <div class="text-[11px] text-neutral-400 line-through mr-2" 
                    v-if="getBestSure(nt?.sure).price_high && getBestSure(nt?.sure).price < getBestSure(nt?.sure).price_high">
                      {{ formatMoney(getBestSure(nt?.sure).price_high) }}
                      {{ getCurrencySymbol(getBestSure(nt?.sure).currency) }}
                    </div>
                    <div class="text-sm font-bold text-black-700">
                      {{ formatMoney(getBestSure(nt?.sure).price) }}
                      {{ getCurrencySymbol(getBestSure(nt?.sure).currency) }}
                    </div>
                  </div>
                </div>
              </div>
            </button>
          </div>
        </article>
      </div>
    </div>

    <div v-else-if="!loading" class="nearest-empty-state">
      <font-awesome-icon icon="fa-solid fa-info-circle" class="mr-2 w-4 h-4 text-accent-600"></font-awesome-icon>
      {{ $t('Tüm programlarımız doldu. Yaz umre programları yakında açıklanacaktır.') }}
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import axios from 'axios'
import { format, parseISO } from 'date-fns'
import { tr } from 'date-fns/locale'
import { Carousel, Slide, Pagination } from 'vue3-carousel'
import 'vue3-carousel/dist/carousel.css'

const { locale } = useI18n()
const isMobile = useIsMobile()
const config = useRuntimeConfig()
const { goPath, showImage, formatMoney } = useCommon()

const loading = ref(false)
const nextTours = ref([])

const visibleTours = computed(() => {
  const list = nextTours.value || []
  return list.slice(0, isMobile.value ? 4 : 3)
})

const getBestSure = (sureList) => {
  if (!Array.isArray(sureList) || sureList.length === 0) return null
  return sureList.reduce((best, current) => {
    if (!best) return current
    if (Number(current?.price || 0) < Number(best?.price || 0)) return current
    return best
  }, null)
}

const getCurrencySymbol = (currency) => {
  if (currency == 'TL') return '₺'
  if (currency == 'EURO') return '€'
  return 'USD'
}

const getDateDay = (dateValue) => {
  if(!dateValue) return ''
  try {
    return format(parseISO(dateValue), 'dd')
  } catch {
    return ''
  }
}

const getDateMonth = (dateValue) => {
  if(!dateValue) return ''
  try {
    const month = locale.value == 'tr'
      ? format(parseISO(dateValue), 'MMM', { locale: tr })
      : format(parseISO(dateValue), 'MMM')
    return month.toUpperCase()
  } catch {
    return ''
  }
}

const getDateWeekday = (dateValue) => {
  if(!dateValue) return ''
  try {
    return locale.value == 'tr'
      ? format(parseISO(dateValue), 'EEEE', { locale: tr })
      : format(parseISO(dateValue), 'EEEE')
  } catch {
    return ''
  }
}

const nextTourFields = `
  pdb_Id
  start
  tur_Sure
  turs_otel1_sure
  turs_otel2_sure
  turs_StartDate
  turs_EndDate
  tour_Currency
  turs_double
  turs_double_high
  turs_child
  turs_baby
  turs_single
  turs_triple
  otel1_photo
  otel2_photo
  airwayLogo
  airwayName {
    tr,
    en
  }
  otel1 {
    sOtelAdi {
      tr,
      en
    }
    city {
      tr,
      en
    }
  }
  otel2 {
    sOtelAdi {
      tr,
      en
    }
    city {
      tr,
      en
    }
  }
`

const getNextTours = () => {
  loading.value = true
  const tday = new Date().toISOString().slice(0, 10)

  axios({
    url: `${config.public.apiBase}/graphql/direct`,
    method: 'post',
    data: {
      query: `
      {
        tours(startLeast: "${tday}", pdb_status:${1}, category:"5YILDIZUMRE", turs_status:"ALLOPEN", sureLeast: "4", sureMost: "21", limit: 18, order: "turs_EndDate ASC") {
          ${nextTourFields}
        }
      }`
    }
  })
    .then(response => {
      const formattedTour = {}

      ;(response?.data?.data?.tours || []).forEach(item => {
        const parsedStartDate = parseISO(item.turs_StartDate)
        const parsedEndDate = parseISO(item.turs_EndDate)

        const dateInfo = {
          start_mini: locale.value == 'tr'
            ? format(parsedStartDate, 'dd MMM, EEE', { locale: tr })
            : format(parsedStartDate, 'dd MMM, EEE'),
          end_mini: locale.value == 'tr'
            ? format(parsedEndDate, 'dd MMM', { locale: tr })
            : format(parsedEndDate, 'dd MMM'),
          turs_StartDate: item.turs_StartDate,
          tour: []
        }

        const hotelOption = {
          otel1_photo: item.otel1_photo,
          otel1: item.otel1?.sOtelAdi?.[locale.value] || item.otel1?.sOtelAdi?.tr,
          otel1_city: item.otel1?.city?.[locale.value] || item.otel1?.city?.tr,
          otel2_photo: item.otel2_photo,
          otel2: item.otel2?.sOtelAdi?.[locale.value] || item.otel2?.sOtelAdi?.tr,
          otel2_city: item.otel2?.city?.[locale.value] || item.otel2?.city?.tr,
          airwayLogo: item.airwayLogo,
          airwayName: item.airwayName?.[locale.value] || item.airwayName?.tr,
          sure: [{
            id: item.pdb_Id,
            sure: item.tur_Sure,
            currency: item.tour_Currency,
            price: Number(item.turs_double || 0),
            price_high: Number(item.turs_double_high || 0),
            turs_child: item.turs_child,
            turs_baby: item.turs_baby,
            turs_single: item.turs_single,
            turs_triple: item.turs_triple,
            end_mini: dateInfo.end_mini
          }]
        }

        const key = item.turs_StartDate

        if (!formattedTour[key]) {
          formattedTour[key] = { ...dateInfo, total: 1, tour: [hotelOption] }
          return
        }

        const hotelIndex = formattedTour[key].tour.findIndex(h =>
          h.otel1 == hotelOption.otel1 && h.otel2 == hotelOption.otel2 && h.airwayName == hotelOption.airwayName
        )

        if (hotelIndex > -1) {
          formattedTour[key].total++
          formattedTour[key].tour[hotelIndex]?.sure.push(hotelOption.sure[0])
        } else {
          formattedTour[key].total++
          formattedTour[key].tour.push(hotelOption)
        }
      })

      nextTours.value = Object.values(formattedTour)
    })
    .finally(() => {
      loading.value = false
    })
}

watch(() => locale.value, () => {
  getNextTours()
})

getNextTours()
</script>

<style scoped>
.nearest-premium-wrap {
  position: relative;  
}

.nearest-premium-wrap::before {
  content: '';
  position: absolute;
  inset: 0;

  pointer-events: none;
}

.nearest-date-card {
  position: relative;
  overflow: hidden;
  border-color: rgba(193, 161, 79, 0.28);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.97) 0%, rgba(253, 250, 243, 0.94) 100%);
  box-shadow: 0 18px 38px -28px rgba(15, 23, 42, 0.6);
  transition: transform 320ms ease, box-shadow 320ms ease, border-color 280ms ease;
}

.nearest-date-card:hover {
  transform: translateY(-4px);
  border-color: rgba(184, 134, 11, 0.46);
  box-shadow: 0 24px 44px -26px rgba(15, 23, 42, 0.68);
}

.nearest-date-card::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(125deg, rgba(184, 134, 11, 0.1) 0%, transparent 48%, rgba(15, 23, 42, 0.08) 100%);
  pointer-events: none;
}

.nearest-date-card::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.38) 48%, transparent 100%);
  transform: translateX(-130%);
  transition: transform 750ms ease;
  pointer-events: none;
}

.nearest-date-card:hover::after {
  transform: translateX(130%);
}

.nearest-option-card:disabled {
  cursor: default;
  opacity: 0.8;
}

.nearest-option-card {
  position: relative;
  overflow: hidden;
  transition: transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease, background-color 220ms ease;
}

.nearest-option-card::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(120deg, rgba(184, 134, 11, 0.1) 0%, transparent 44%);
  opacity: 0;
  transition: opacity 220ms ease;
  pointer-events: none;
}

.nearest-option-card:hover {
  transform: translateY(-2px);
  border-color: rgba(184, 134, 11, 0.45);
  background: linear-gradient(180deg, rgba(255, 255, 255, 1) 0%, rgba(255, 250, 236, 0.9) 100%);
  box-shadow: 0 12px 24px -18px rgba(15, 23, 42, 0.6);
}

.nearest-option-card:hover::before {
  opacity: 1;
}

.nearest-date-head {
  border-color: rgba(255, 255, 255, 0.3);
  background: linear-gradient(95deg, rgba(161, 158, 154, 0.88) 0%, rgba(173, 149, 97, 0.22) 42%, rgba(255, 255, 255, 0.9) 100%);
}

.nearest-allprices-btn {
  box-shadow: 0 14px 28px -18px rgba(164, 147, 98, 0.82);
}

.nearest-option-list {
  scrollbar-width: thin;
  scrollbar-color: #d9e1ea transparent;
  max-height: 150px;
}

.nearest-option-list::-webkit-scrollbar {
  width: 6px;
}

.nearest-option-list::-webkit-scrollbar-thumb {
  background: #d9e1ea;
  border-radius: 999px;
}

.nearest-mobile-carousel :deep(.carousel__pagination) {
  min-height: 32px;
  margin-top: 5px;
  gap: 6px;
  align-items: center;
}

.nearest-mobile-carousel :deep(.carousel__pagination-button) {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #d9e1ea;
  border: 1px solid #cbd5e1;
}

.nearest-mobile-carousel :deep(.carousel__pagination-button--active) {
  width: 20px;
  background: #b47a16;
  border-color: #936212;
}

.nearest-mobile-carousel :deep(.carousel__pagination-button::after) {
  display: none;
}

.nearest-mobile-carousel :deep(.carousel__slide) {
  align-items: stretch;
}

/* Programme comparison surface */
.nearest-premium-wrap {
  display: block;
  overflow: visible !important;
  padding-top: 1rem;
}

.nearest-program-hero {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.25rem;
  overflow: hidden;
  margin-bottom: 1rem;
  border: 1px solid rgba(225, 194, 115, 0.32);
  border-radius: 1.5rem;
  padding: 1.25rem 1.35rem;
  background:
    radial-gradient(circle at 88% 0%, rgba(207, 164, 78, 0.24), transparent 28%),
    radial-gradient(circle at 7% 112%, rgba(26, 137, 125, 0.28), transparent 48%),
    linear-gradient(120deg, #10273a 0%, #12384a 52%, #0c2430 100%);
  box-shadow: 0 20px 42px -32px rgba(15, 31, 49, 0.8);
}

.nearest-program-hero::after {
  position: absolute;
  right: -4rem;
  bottom: -6rem;
  width: 15rem;
  height: 15rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 999px;
  content: '';
  pointer-events: none;
}

.nearest-program-copy { position: relative; z-index: 1; min-width: 0; }

.nearest-program-kicker {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  margin: 0 0 0.42rem;
  color: #f5d585;
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.nearest-program-title {
  margin: 0;
  color: #fff;
  font-size: clamp(1.25rem, 2.4vw, 1.65rem);
  font-weight: 760;
  line-height: 1.17;
  letter-spacing: -0.025em;
}

.nearest-program-description {
  max-width: 38rem;
  margin: 0.42rem 0 0;
  color: rgba(226, 238, 242, 0.82);
  font-size: 0.79rem;
  line-height: 1.5;
}

.nearest-allprices-btn {
  position: relative;
  z-index: 1;
  display: inline-flex;
  flex: none;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid rgba(255, 232, 173, 0.62);
  border-radius: 0.85rem;
  padding: 0.72rem 0.95rem;
  background: linear-gradient(135deg, #d5a749, #ad751f);
  box-shadow: 0 14px 26px -18px rgba(0, 0, 0, 0.72), inset 0 1px 0 rgba(255, 255, 255, 0.24);
  color: #172033;
  font-size: 0.78rem;
  font-weight: 800;
  transition: transform 180ms ease, box-shadow 180ms ease, filter 180ms ease;
}

.nearest-allprices-btn:hover { transform: translateY(-2px); filter: brightness(1.06); box-shadow: 0 18px 30px -18px rgba(0, 0, 0, 0.78); }
.nearest-allprices-btn:focus-visible { outline: 3px solid #f5d585; outline-offset: 3px; }

.nearest-loading-state,
.nearest-empty-state {
  display: flex;
  min-height: 9rem;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  border: 1px solid #dce5e9;
  border-radius: 1.25rem;
  background: #fff;
  color: #526274;
  font-size: 0.86rem;
}

.nearest-program-content { width: 100%; }

.nearest-program-content > .grid {
  gap: 1rem;
}

.nearest-date-card {
  display: flex;
  min-height: 100%;
  flex-direction: column;
  border: 1px solid #dce6e9;
  border-radius: 1.25rem;
  padding: 0.72rem;
  background: linear-gradient(160deg, #fff 0%, #f8fbfb 100%);
  box-shadow: 0 18px 32px -28px rgba(15, 31, 49, 0.58);
}

.nearest-date-card::before,
.nearest-date-card::after { display: none; }

.nearest-date-card:hover {
  transform: translateY(-3px);
  border-color: rgba(15, 118, 110, 0.4);
  box-shadow: 0 24px 38px -28px rgba(15, 31, 49, 0.62);
}

.nearest-date-head {
  min-height: 4.15rem;
  margin: 0 0 0.7rem;
  border: 1px solid rgba(15, 118, 110, 0.16);
  border-radius: 0.95rem;
  padding: 0.65rem 0.72rem;
  background: linear-gradient(110deg, rgba(15, 70, 77, 0.1), rgba(255, 255, 255, 0.96));
}

.nearest-date-head > div:first-child > div:first-child {
  min-width: 3.15rem;
  border: 1px solid rgba(15, 118, 110, 0.24);
  border-radius: 0.72rem;
  padding: 0.4rem;
  background: #fff;
  box-shadow: none;
}

.nearest-date-head > div:first-child > div:first-child > div:first-child {
  color: #0f4d51;
  font-size: 1.1rem;
}

.nearest-date-head > div:first-child > div:first-child > div:last-child { color: #aa751f; }
.nearest-date-head > div:first-child > div:nth-child(2) > div:first-child { color: #183b4b; }
.nearest-date-head > div:first-child > div:nth-child(2) > div:last-child { color: #64748b; }

.nearest-date-head > div:last-child {
  border-color: rgba(170, 117, 31, 0.2);
  background: rgba(245, 213, 133, 0.22);
  color: #78511a;
}

.nearest-option-list {
  display: grid;
  flex: 1;
  gap: 0.58rem;
  max-height: 17rem;
  padding-right: 0.15rem;
}

.nearest-option-card {
  min-height: 8.65rem;
  border: 1px solid #e0e9eb;
  border-radius: 0.95rem;
  padding: 0.72rem;
  background: #fff;
  box-shadow: none;
}

.nearest-option-card::before { background: linear-gradient(120deg, rgba(15, 118, 110, 0.08), transparent 45%); }
.nearest-option-card:hover { border-color: rgba(15, 118, 110, 0.38); background: #fff; box-shadow: 0 14px 24px -20px rgba(15, 31, 49, 0.62); }
.nearest-option-card:focus-visible { outline: 2px solid #0f766e; outline-offset: 2px; }

.nearest-option-card > div:first-child {
  display: flex;
  min-height: 1.2rem;
  align-items: center;
  gap: 0.45rem;
  margin: 0 0 0.56rem;
}

.nearest-option-card > div:first-child > span:first-child {
  margin: 0;
  border: 0;
  border-radius: 0;
  padding: 0;
  background: transparent;
  color: #0f6260;
  font-size: 0.66rem;
  font-weight: 800;
}

.nearest-option-card > div:first-child > span:nth-child(2) { margin-left: auto; }
.nearest-option-card > div:first-child img { max-width: 4.6rem; object-fit: contain; }

.nearest-option-card > div:nth-child(2) { gap: 0.62rem; margin: 0 0 0.58rem; }
.nearest-option-card > div:nth-child(2) > div:first-child { gap: 0; }
.nearest-option-card > div:nth-child(2) img { width: 2.6rem; height: 2.6rem; border-radius: 0.72rem; object-fit: cover; box-shadow: none; }
.nearest-option-card > div:nth-child(2) img + img { margin-left: -0.7rem; border: 2px solid #fff; }
.nearest-option-card > div:nth-child(2) > div:last-child > div { color: #263647; font-size: 0.72rem; line-height: 1.45; }
.nearest-option-card > div:nth-child(2) > div:last-child > div > span:first-child { color: #77889a; font-weight: 500; }

.nearest-option-card > div:last-child {
  border-color: #e2ebed;
  border-radius: 0.7rem;
  padding: 0.48rem 0.58rem;
  background: #f8fbfb;
}

.nearest-option-card > div:last-child > div:first-child { color: #627386; font-size: 0.67rem; }
.nearest-option-card > div:last-child > div:first-child > span:first-child { color: #253b4b; }
.nearest-option-card > div:last-child > div:last-child > div:last-child { color: #0f5c59; font-size: 0.82rem; }

.nearest-quality {
  display: inline-flex;
  align-items: center;
  border: 1px solid rgba(170, 117, 31, 0.2);
  border-radius: 999px;
  padding: 0.18rem 0.38rem;
  background: rgba(245, 213, 133, 0.2);
  color: #a36e17;
  font-size: 0.62rem;
  font-weight: 800;
}

@media (max-width: 767px) {
  .nearest-premium-wrap { padding-top: 0.65rem; padding-bottom: 1.25rem; }
  .nearest-program-hero { align-items: flex-start; border-radius: 1.25rem; padding: 1rem; }
  .nearest-program-title { font-size: 1.18rem; }
  .nearest-program-description { max-width: 13.5rem; font-size: 0.71rem; }
  .nearest-allprices-btn { align-self: flex-start; padding: 0.64rem; font-size: 0; }
  .nearest-allprices-btn svg { width: 1rem; height: 1rem; }
  .nearest-date-card { margin: 0 0.2rem; }
  .nearest-option-list { max-height: 18rem; }
}

@media (prefers-reduced-motion: reduce) {
  .nearest-date-card::after {
    transition: none;
  }
}
</style>
